// @flow
import React from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/styles';
import {Divider, Grid, Radio, Typography} from '@material-ui/core';
import {colors} from '../../../../../assets/styles/colors';
import IosSwitch from '../../../../../elements/IosSwitch/IosSwitch';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';

import {ImageButton} from '../ImageButton/ImageButton';

import type {StoreInformation} from '../../../../../entities';
import InternalPageRouteAutocomplete from '../../../../../elements/InternalPageRouteAutocomplete';
import {useStoreBusinessModel} from '../../../../store/utils';

type Props = {
  missingCover?: boolean,
  missingLogo?: boolean,
  storeInformation?: StoreInformation,
  register: (value: any) => void,
  errors: {[key: string]: any},
  logoShape: string,
  setLogoShape: (type: string) => void,
  descriptionShow: boolean,
  coverShow: boolean,
  setDescriptionShow: (value: boolean) => void,
  setCoverShow: (value: boolean) => void,
  coverImage?: string | null,
  setCoverImage: (img: string | null) => void,
  coverImageFile?: File | null,
  setCoverImageFile: (f: File | null) => void,
  logoImage?: string | null,
  setLogoImage: (img: string | null) => void,
  logoImageFile?: File | null,
  setLogoImageFile: (f: File | null) => void,
  navigationButtonShow: boolean,
  setNavigationButtonShow: (value: boolean) => void,
  navigationButtonText?: string,
  setNavigationButtonText: (value: string) => void,
  navigationButtonUrl?: string,
  setNavigationButtonUrl: (value: string) => void,
};

const useStyles = makeStyles(theme => ({
  coverSection: {},
  divider: {
    margin: theme.spacing(1, 0, 2, 0),
    backgroundColor: '#E8E8ED',
  },
  wrapRadioInput: {},
  noSelected: {
    color: colors.grey700,
  },
  rowEnableSetting: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  helperText: {
    fontSize: 14,
    color: colors.grey900,
    fontWeight: 500,
  },
  imageBoxDashed: {
    height: '100%',
    border: `2px dashed ${colors.grey500}`,
    boxSizing: 'border-box',
    borderRadius: '12px',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: colors.purple3,
    '&:focus-within': {
      border: `2px dashed ${theme.palette.primary.main}`,
    },
  },
  gridItemMobile: {
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(2, 0),
    },
  },
  defaultCursor: {
    cursor: 'unset',
  },
  imageBox: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #E8E8E9',
    boxSizing: 'border-box',
    borderRadius: theme.borderRadius,
    padding: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
  },
  image: {
    margin: 'auto',
    borderRadius: '50%',
    maxWidth: '100%',
  },
  removeImageIcon: {
    position: 'absolute',
    top: 4,
    right: 4,
  },
  imageUploadTip: {
    textAlign: 'center',
    fontSize: '0.875rem',
    color: colors.purple3,
  },
  errorText: {
    fontSize: 16,
    [theme.breakpoints.up('md')]: {
      fontSize: 14,
    },
    color: '#f44336',
  },
}));

export const LOGO_SHAPES = {
  SQUARE: {
    aspect: 1,
    width: 600,
    height: 600,
  },
  RECTANGLE: {
    aspect: 3.3,
    width: 2000,
    height: 600,
  },
  CIRCLE: {
    aspect: 1,
    width: 600,
    height: 600,
  },
};

export default function CoverSection(props: Props) {
  const {
    missingCover,
    missingLogo,
    storeInformation,
    register,
    errors,
    logoShape,
    setLogoShape,
    descriptionShow,
    coverShow,
    setDescriptionShow,
    setCoverShow,
    coverImage,
    setCoverImage,
    coverImageFile,
    setCoverImageFile,
    logoImage,
    setLogoImage,
    logoImageFile,
    setLogoImageFile,
    navigationButtonShow,
    setNavigationButtonShow,
    navigationButtonText,
    setNavigationButtonText,
    navigationButtonUrl,
    setNavigationButtonUrl,
  } = props;

  const classes = useStyles();

  const businessModel = useStoreBusinessModel();

  const handleLogoShape = e => {
    setLogoShape(e.target.value);
  };

  const logoAspectRatio = LOGO_SHAPES[logoShape]?.aspect || 1;

  return (
    <div className={classes.coverSection}>
      <Grid container>
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={12} md={6}>
              <Typography className={classes.helperText}>
                Seleccionar la forma de tu logo
              </Typography>
              {missingLogo && (
                <Typography className={classes.errorText}>
                  Debes agregar el logo de tu tienda.
                </Typography>
              )}
              <div className={classes.wrapRadioInput}>
                <Radio
                  id="shape-0"
                  color="primary"
                  type="radio"
                  value={'SQUARE'}
                  checked={logoShape === 'SQUARE'}
                  onChange={handleLogoShape}
                />
                <label
                  htmlFor="shape-0"
                  className={`${classes.radioButtonLabel} ${
                    logoShape !== 'SQUARE' ? classes.noSelected : ''
                  }`}
                >
                  Cuadrada
                </label>
              </div>
              <div className={classes.wrapRadioInput}>
                <Radio
                  id="shape-1"
                  color="primary"
                  type="radio"
                  value={'RECTANGLE'}
                  checked={logoShape === 'RECTANGLE'}
                  onChange={handleLogoShape}
                />
                <label
                  htmlFor="shape-1"
                  className={`${classes.radioButtonLabel} ${
                    logoShape !== 'RECTANGLE' ? classes.noSelected : ''
                  }`}
                >
                  Rectangular
                </label>
              </div>
              <div className={classes.wrapRadioInput}>
                <Radio
                  id="shape-2"
                  color="primary"
                  type="radio"
                  value={'CIRCLE'}
                  checked={logoShape === 'CIRCLE'}
                  onChange={handleLogoShape}
                />
                <label
                  htmlFor="shape-2"
                  className={`${classes.radioButtonLabel} ${
                    logoShape !== 'CIRCLE' ? classes.noSelected : ''
                  }`}
                >
                  Circular
                </label>
              </div>
            </Grid>
            <Grid item xs={12} md={6} className={classes.gridItemMobile}>
              <ImageButton
                placeholder="Sube el logo de tu tienda"
                image={logoImage}
                setImage={setLogoImage}
                imageFile={logoImageFile}
                setImageFile={setLogoImageFile}
                previewStyle={logoShape === 'CIRCLE' ? classes.image : ''}
                keepFormat
                id="logo-input-file"
                recommendedWidth={LOGO_SHAPES[logoShape]?.width}
                recommendedHeight={LOGO_SHAPES[logoShape]?.height}
                aspect={logoAspectRatio}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Divider className={classes.divider} />
      {businessModel === 'PRODUCT' && (
        <div>
          <div className={classes.rowEnableSetting}>
            <Typography className={classes.helperText}>
              Foto de portada:
            </Typography>
            <IosSwitch
              name="cover"
              checked={coverShow}
              onChange={e => setCoverShow(e.target.checked)}
            />
          </div>
          {missingCover && (
            <Typography className={classes.errorText}>
              Debes agregar la imagen de portada.
            </Typography>
          )}
          {coverShow && (
            <div>
              <ImageButton
                placeholder="Sube foto de portada
              (1000 x 250 px)"
                hideRecommendSize
                image={coverImage}
                setImage={setCoverImage}
                imageFile={coverImageFile}
                setImageFile={setCoverImageFile}
                maxWidth={3840}
                maxHeight={2160}
                aspect={4}
                id="cover-input-file"
              />
            </div>
          )}
        </div>
      )}
      {businessModel === 'SERVICE' && (
        <>
          <Divider className={classes.divider} />
          <div>
            <div className={classes.rowEnableSetting}>
              <Typography className={classes.helperText}>
                Botón de navegación:
              </Typography>
              <IosSwitch
                name="cover"
                checked={navigationButtonShow}
                onChange={e => setNavigationButtonShow(e.target.checked)}
              />
            </div>
            {navigationButtonShow && (
              <div>
                <CustomTextField
                  label="Texto del botón"
                  fullWidth
                  value={navigationButtonText}
                  onChange={e => setNavigationButtonText(e.target.value)}
                />
                <br />
                <br />
                <InternalPageRouteAutocomplete
                  onSelectUrl={urlOption => {
                    if (typeof urlOption === 'string') {
                      setNavigationButtonUrl(urlOption);
                    } else {
                      setNavigationButtonUrl(urlOption.url);
                    }
                  }}
                  freeSolo
                  defaultValue={navigationButtonUrl}
                />
                <br />
              </div>
            )}
          </div>
        </>
      )}
      <Divider className={classes.divider} />
      <div>
        <div className={classes.rowEnableSetting}>
          <Typography className={classes.helperText}>
            Descripción de tienda:
          </Typography>
          <IosSwitch
            name="description"
            checked={descriptionShow}
            onChange={e => setDescriptionShow(e.target.checked)}
          />
        </div>
        {descriptionShow && (
          <CustomTextField
            fullWidth
            multiline
            id="store-description"
            name="storeDescription"
            autoComplete="store-description"
            error={!!errors.storeDescription}
            helperText={
              errors.storeDescription ? errors.storeDescription.message : ''
            }
            defaultValue={storeInformation ? storeInformation.description : ''}
            type="text"
            inputRef={register({
              required: 'Campo obligatorio',
            })}
            rows={6}
            // disabled={isSaving || !hasProfileInfoFeature}
            className={classes.descriptionWrap}
          />
        )}
      </div>
    </div>
  );
}

CoverSection.propTypes = {
  missingCover: PropTypes.bool,
  missingLogo: PropTypes.bool,
  storeInformation: PropTypes.object,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  logoShape: PropTypes.any,
  setLogoShape: PropTypes.func.isRequired,
  coverShow: PropTypes.bool.isRequired,
  descriptionShow: PropTypes.bool.isRequired,
  setDescriptionShow: PropTypes.func.isRequired,
  setCoverShow: PropTypes.func.isRequired,
  coverImage: PropTypes.string,
  setCoverImage: PropTypes.func.isRequired,
  coverImageFile: PropTypes.any,
  setCoverImageFile: PropTypes.func.isRequired,
  logoImage: PropTypes.string,
  setLogoImage: PropTypes.func.isRequired,
  logoImageFile: PropTypes.any,
  setLogoImageFile: PropTypes.func.isRequired,
};
